import {
	AdjustmentsHistoryRecord,
	defaultCullingSettings,
	HistoryRecordAction,
	PhotoRecord,
} from "@/lib/catalog/types"
import { getCullingSettingsEventProperties } from "../importing/culling-event-utils"
import { allPhotosAtom } from "../photo/photos-state"
import { projectAtom } from "../project/projects-state"
import { getProjectModeTimeSpentMinutes } from "../project/project-session-timing"
import { store } from "../store"
import { getExportStyleMode } from "../style/style-event-utils"
import { currentStyleAtom } from "../style/style-state"

const toRatio = (numerator: number, denominator: number) => {
	if (!denominator) {
		return 0
	}

	return Number((numerator / denominator).toFixed(2))
}

const toPer1k = (numerator: number, denominator: number) => {
	if (!denominator) {
		return 0
	}

	return Number(((numerator * 1000) / denominator).toFixed(2))
}

export const getIsAIEditOnlyFromHistory = (historyRecord?: AdjustmentsHistoryRecord) => {
	return Boolean(
		historyRecord &&
		historyRecord.styleId != null &&
		historyRecord.action === HistoryRecordAction.ApplyAIStyle,
	)
}

export const getExportCompleteEventProperties = (options: {
	destination: "local" | "pixieset"
	projectId?: string
	exportedPhotos: PhotoRecord[]
	completedCount: number
	aiEditOnlyCount: number
	collectionId?: string | null
	collectionName?: string | null
}) => {
	const {
		destination,
		projectId,
		exportedPhotos,
		completedCount,
		aiEditOnlyCount,
		collectionId,
		collectionName,
	} = options

	const project = store.get(projectAtom)
	const style = store.get(currentStyleAtom)
	const allPhotos = store.get(allPhotosAtom)
	const cullingSettings = project?.cullingSettings ?? defaultCullingSettings

	const totalAIKeepers = allPhotos.filter((photo) => photo.smartCullingTag === "Keeper").length
	const totalAICandidates = allPhotos.filter(
		(photo) => photo.smartCullingTag === "Candidate",
	).length
	const totalAIRejects = allPhotos.filter((photo) => photo.smartCullingTag === "Reject").length

	const exportedAIKeepers = exportedPhotos.filter(
		(photo) => photo.smartCullingTag === "Keeper",
	).length
	const exportedAIRejects = exportedPhotos.filter(
		(photo) => photo.smartCullingTag === "Reject",
	).length
	const totalReferenceCount = style?.referenceCount ?? 0

	const { cullingTimeSpentMinutes, editingTimeSpentMinutes } =
		getProjectModeTimeSpentMinutes(projectId)

	return {
		projectId: projectId ?? null,
		destination,
		...(destination === "pixieset"
			? {
					collectionId: collectionId ?? null,
					collectionName: collectionName ?? null,
				}
			: {}),
		styleId: style?.styleId ?? null,
		completed: completedCount,
		totalCount: exportedPhotos.length,
		aiEditOnlyCount,
		KeeperExportRate: toRatio(exportedAIKeepers, totalAIKeepers),
		KeeperShareOfExports: toRatio(exportedAIKeepers, exportedPhotos.length),
		RejectExportRate: toRatio(exportedAIRejects, totalAIRejects),
		RejectShareOfExports: toRatio(exportedAIRejects, exportedPhotos.length),
		...getCullingSettingsEventProperties({
			cullingSettings,
			suggestKeepersCount: totalAIKeepers,
			includeAutoAdvance: true,
		}),
		suggestCandidatesCount: totalAICandidates,
		suggestRejectsCount: totalAIRejects,
		totalReferenceCount,
		referenceEditsPer1kExportedPhotos: toPer1k(totalReferenceCount, exportedPhotos.length),
		styleMode: getExportStyleMode({ style, isManual: Boolean(project?.isManual) }),
		styleName: style?.name ?? null,
		cullingTimeSpentMinutes,
		editingTimeSpentMinutes,
		aiEditRate: toRatio(aiEditOnlyCount, exportedPhotos.length),
	}
}
